import type { BarState, ScheduleBar } from '../types.js';

/**
 * 316 (FR-017, research R11) — what a schedule bar looks like and what it is called.
 *
 * Ten display states, one map. The legend is generated from the same map, so a state cannot be
 * drawn without being explained.
 *
 * 🔴 Constitution V: no two states may differ by colour alone. CSS offers only four visible border
 * styles, so each state's non-colour TREATMENT is its border style, border width and hatch pattern
 * together (`treatmentOf`), and the tests require every pair to differ in it.
 *
 * 🔴 The status comes from the BOOKING (`booking_status`), not from the hold. A hold is `committed`
 * for confirmed, checked-out and checked-in bookings alike, which is how the old screen labelled a
 * car out on the road as "Confirmed".
 */

export const DISPLAY_STATES = [
  'confirmed',
  'awaiting_payment',
  'checked_out',
  'checked_in',
  'likely_abandoned',
  'no_hold',
  'blocked',
  'maintenance',
  'inspection',
  'other',
] as const;

export type DisplayState = (typeof DISPLAY_STATES)[number];

export const WORD: Record<DisplayState, string> = {
  confirmed: 'Confirmed',
  awaiting_payment: 'Awaiting payment',
  checked_out: 'Checked out',
  checked_in: 'Checked in',
  likely_abandoned: 'Abandoned?',
  // 286: the car is NOT held for this window, unlike an awaiting-payment hold (always real).
  no_hold: 'No hold',
  blocked: 'Blocked',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  other: 'Other',
};

// Literal class strings, so Tailwind's scanner sees every one. Treatment (style / width / pattern):
// confirmed solid/1, awaiting dashed/1, checked out solid/2, checked in dashed/2, abandoned
// dotted/1, no hold double/4, blocked solid/1 hatched, maintenance dotted/2, inspection dotted/1
// hatched, other dashed/1 hatched.
export const STYLE: Record<DisplayState, string> = {
  confirmed: 'border border-solid bg-brand-blue-soft border-brand-blue text-ink',
  // 349 (#951): faded (70%, so the text keeps AA contrast) — it holds the car, but is not a rental yet.
  awaiting_payment: 'border border-dashed bg-canvas border-brand-blue-mid text-ink opacity-70',
  checked_out: 'border-2 border-solid bg-primary border-primary-deep text-on-primary',
  checked_in:
    'border-2 border-dashed bg-semantic-success-bg border-semantic-success-text text-semantic-success-text',
  likely_abandoned:
    'border border-dotted bg-semantic-warning-bg border-semantic-warning-text text-semantic-warning-text',
  no_hold: 'border-4 border-double bg-canvas border-steel text-steel',
  blocked:
    'border border-solid bar-hatch bg-semantic-danger-bg border-semantic-danger-text text-semantic-danger-text',
  maintenance: 'border-2 border-dotted bg-accent-orange-soft border-accent-orange text-ink',
  inspection: 'border border-dotted bar-hatch bg-accent-purple-soft border-accent-purple text-ink',
  other: 'border border-dashed bar-hatch bg-surface-soft border-steel text-slate',
};

/** The hold state each display state belongs to — kept on legend entries for the 096 legend test. */
export const HOLD_STATE: Record<DisplayState, BarState> = {
  confirmed: 'committed',
  awaiting_payment: 'tentative',
  checked_out: 'committed',
  checked_in: 'committed',
  likely_abandoned: 'likely_abandoned',
  no_hold: 'unheld',
  blocked: 'non_booking',
  maintenance: 'non_booking',
  inspection: 'non_booking',
  other: 'non_booking',
};

const KIND_STATE: Partial<Record<ScheduleBar['kind'], DisplayState>> = {
  manual_block: 'blocked',
  maintenance: 'maintenance',
  inspection: 'inspection',
  other: 'other',
};

export function displayStateOf(bar: ScheduleBar): DisplayState {
  const kindState = KIND_STATE[bar.kind];
  if (kindState) return kindState;
  // Facts about the hold itself win: "nobody may be coming" and "the car is not held" matter more
  // to an operator than the status the booking claims.
  if (bar.state === 'likely_abandoned') return 'likely_abandoned';
  if (bar.state === 'unheld') return 'no_hold';
  if (bar.booking_status === 'checked_out') return 'checked_out';
  if (bar.booking_status === 'checked_in') return 'checked_in';
  if (bar.booking_status === 'awaiting_payment') return 'awaiting_payment';
  if (bar.booking_status === null && bar.state === 'tentative') return 'awaiting_payment';
  return 'confirmed';
}

function statusWord(status: string): string {
  const words = status.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface BarStyle {
  state: DisplayState;
  word: string;
  className: string;
}

export function barStyleFor(bar: ScheduleBar): BarStyle {
  const state = displayStateOf(bar);
  // A hold kept by a booking in any other status (a completed rental keeps its hold) is drawn as
  // confirmed but named by its real status (FR-017: labels are the booking's real status).
  const word =
    state === 'confirmed' && bar.booking_status && bar.booking_status !== 'confirmed'
      ? statusWord(bar.booking_status)
      : WORD[state];
  return { state, word, className: STYLE[state] };
}

/**
 * #631 — the bars that carry a warning mark: a car late back, a hold that looks abandoned, and a
 * booking whose car is not held. Each is something to act on, and the mark says so without colour.
 */
export function needsAttention(bar: ScheduleBar): boolean {
  if (bar.overdue) return true;
  // 416 (#1431): a rented car outside its zone needs a look too.
  if (bar.outside_zone) return true;
  const state = displayStateOf(bar);
  return state === 'likely_abandoned' || state === 'no_hold';
}

/** The non-colour part of a class string: what a colour-blind operator can still tell apart. */
export function treatmentOf(className: string): {
  style: string;
  width: number;
  pattern: string;
  key: string;
} {
  const tokens = className.split(/\s+/);
  const style =
    tokens.find((t) => /^border-(solid|dashed|dotted|double)$/.test(t))?.slice('border-'.length) ??
    '';
  const widthToken = tokens.find((t) => /^border-[0-9]+$/.test(t));
  const width = widthToken
    ? Number(widthToken.slice('border-'.length))
    : tokens.includes('border')
      ? 1
      : 0;
  const pattern = tokens.includes('bar-hatch') ? 'hatch' : '';
  return { style, width, pattern, key: `${style}/${width}/${pattern}` };
}

/**
 * 386 slice 2 (#1278, the approved mock-up): the gap kept after a rental — a light blue hatch with a dashed edge, so it
 * reads as "not bookable" without looking like a bar. Shared by the schedule row and the legend.
 */
export const GAP_STYLE =
  'border border-dashed border-brand-blue-mid bg-[repeating-linear-gradient(135deg,var(--color-brand-blue-soft)_0,var(--color-brand-blue-soft)_6px,var(--color-surface-feature)_6px,var(--color-surface-feature)_12px)]';
